/**
 * TapToPayDialog.js
 * Tap-to-Pay Dialog für EC-Zahlung vor Ort (Nexi SoftPOS Mock)
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  CreditCard,
  Smartphone,
  CheckCircle,
  XCircle,
  Loader2,
  Wifi,
  Euro,
  Gift
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const TapToPayDialog = ({ open, onClose, order, onPaymentComplete }) => {
  const [step, setStep] = useState('amount'); // amount, processing, success, failed
  const [amount, setAmount] = useState('');
  const [tipAmount, setTipAmount] = useState('');
  const [transactionId, setTransactionId] = useState(null);
  const [processingTime, setProcessingTime] = useState(0);

  useEffect(() => {
    if (open && order) {
      setAmount(order.total_gross?.toFixed(2) || '');
      setTipAmount('');
      setStep('amount');
      setTransactionId(null);
    }
  }, [open, order]);

  // Processing Animation Timer
  useEffect(() => {
    let timer;
    if (step === 'processing') {
      timer = setInterval(() => {
        setProcessingTime(prev => prev + 1);
      }, 1000);
    } else {
      setProcessingTime(0);
    }
    return () => clearInterval(timer);
  }, [step]);

  const handleInitiatePayment = async () => {
    const amountNum = parseFloat(amount) || 0;
    const tipNum = parseFloat(tipAmount) || 0;

    if (amountNum <= 0) {
      toast.error('Bitte gültigen Betrag eingeben');
      return;
    }

    setStep('processing');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/payments/tap-to-pay/initiate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          order_id: order.source_order_id || order.cashx_order_id,
          amount: amountNum,
          tip_amount: tipNum
        })
      });

      const data = await response.json();

      if (data.success) {
        setTransactionId(data.transaction_id);
        
        // Mock: Nach 3 Sekunden "Zahlung erfolgreich"
        setTimeout(async () => {
          await completePayment(data.transaction_id);
        }, 3000);
      } else {
        setStep('failed');
        toast.error(data.detail || 'Fehler beim Starten der Zahlung');
      }
    } catch (error) {
      console.error('Payment error:', error);
      setStep('failed');
      toast.error('Verbindungsfehler');
    }
  };

  const completePayment = async (txId) => {
    try {
      const response = await fetch(`${API_URL}/api/payments/tap-to-pay/callback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          transaction_id: txId,
          status: 'success'
        })
      });

      const data = await response.json();

      if (data.success) {
        setStep('success');
        toast.success('Zahlung erfolgreich! ✅');
        setTimeout(() => {
          onPaymentComplete?.({
            transaction_id: txId,
            amount: parseFloat(amount),
            tip_amount: parseFloat(tipAmount) || 0,
            method: 'card_onsite'
          });
          onClose();
        }, 2000);
      } else {
        setStep('failed');
      }
    } catch (error) {
      setStep('failed');
    }
  };

  const handleClose = () => {
    if (step !== 'processing') {
      onClose();
    }
  };

  const totalAmount = (parseFloat(amount) || 0) + (parseFloat(tipAmount) || 0);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <CreditCard className="h-6 w-6 text-blue-500" />
            Tap-to-Pay
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            {order?.order_number && `Bestellung #${order.order_number}`}
          </DialogDescription>
        </DialogHeader>

        {/* Step: Amount Input */}
        {step === 'amount' && (
          <div className="space-y-6 py-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Betrag</Label>
              <div className="relative">
                <Euro className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-500" />
                <Input
                  type="number"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="pl-10 h-14 text-2xl font-bold bg-zinc-950 border-zinc-700 text-white"
                  placeholder="0.00"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-300 flex items-center gap-2">
                <Gift className="h-4 w-4 text-amber-500" />
                Trinkgeld (optional)
              </Label>
              <div className="relative">
                <Euro className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-500" />
                <Input
                  type="number"
                  step="0.01"
                  value={tipAmount}
                  onChange={(e) => setTipAmount(e.target.value)}
                  className="pl-10 h-12 bg-zinc-950 border-zinc-700 text-white"
                  placeholder="0.00"
                />
              </div>
              <div className="flex gap-2">
                {[1, 2, 3, 5].map((tip) => (
                  <Button
                    key={tip}
                    variant="outline"
                    size="sm"
                    onClick={() => setTipAmount(tip.toFixed(2))}
                    className="border-zinc-700 text-zinc-400 hover:bg-zinc-800"
                  >
                    +{tip}€
                  </Button>
                ))}
              </div>
            </div>

            {/* Total */}
            <div className="bg-zinc-950 rounded-lg p-4 border border-zinc-800">
              <div className="flex justify-between items-center">
                <span className="text-zinc-400">Gesamt zu zahlen</span>
                <span className="text-3xl font-bold text-white">
                  {totalAmount.toFixed(2)} €
                </span>
              </div>
            </div>

            <Button
              onClick={handleInitiatePayment}
              disabled={!amount || parseFloat(amount) <= 0}
              className="w-full h-14 text-lg bg-blue-600 hover:bg-blue-700"
            >
              <Smartphone className="h-5 w-5 mr-2" />
              Zahlung starten
            </Button>
          </div>
        )}

        {/* Step: Processing */}
        {step === 'processing' && (
          <div className="py-12 text-center space-y-6">
            <div className="relative mx-auto w-32 h-32">
              {/* Phone Animation */}
              <div className="absolute inset-0 bg-blue-500/20 rounded-2xl animate-pulse" />
              <div className="absolute inset-2 bg-zinc-800 rounded-xl flex items-center justify-center">
                <Smartphone className="h-12 w-12 text-blue-400" />
              </div>
              {/* NFC Waves */}
              <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                <Wifi className="h-8 w-8 text-blue-500 animate-bounce" />
              </div>
            </div>

            <div>
              <h3 className="text-xl font-bold text-white mb-2">
                Karte an Gerät halten
              </h3>
              <p className="text-zinc-400">
                Warte auf NFC-Verbindung...
              </p>
            </div>

            <div className="flex items-center justify-center gap-2 text-blue-400">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>{processingTime}s</span>
            </div>

            <div className="bg-zinc-950 rounded-lg p-3 border border-zinc-800">
              <p className="text-zinc-500 text-sm">Zu zahlen</p>
              <p className="text-2xl font-bold text-white">{totalAmount.toFixed(2)} €</p>
            </div>
          </div>
        )}

        {/* Step: Success */}
        {step === 'success' && (
          <div className="py-12 text-center space-y-6">
            <div className="mx-auto w-24 h-24 bg-green-500/20 rounded-full flex items-center justify-center">
              <CheckCircle className="h-16 w-16 text-green-500" />
            </div>

            <div>
              <h3 className="text-2xl font-bold text-green-400 mb-2">
                Zahlung erfolgreich!
              </h3>
              <p className="text-zinc-400">
                {totalAmount.toFixed(2)} € wurden bezahlt
              </p>
            </div>

            {transactionId && (
              <p className="text-xs text-zinc-600">
                ID: {transactionId}
              </p>
            )}
          </div>
        )}

        {/* Step: Failed */}
        {step === 'failed' && (
          <div className="py-12 text-center space-y-6">
            <div className="mx-auto w-24 h-24 bg-red-500/20 rounded-full flex items-center justify-center">
              <XCircle className="h-16 w-16 text-red-500" />
            </div>

            <div>
              <h3 className="text-2xl font-bold text-red-400 mb-2">
                Zahlung fehlgeschlagen
              </h3>
              <p className="text-zinc-400">
                Bitte versuche es erneut
              </p>
            </div>

            <Button
              onClick={() => setStep('amount')}
              variant="outline"
              className="border-zinc-700"
            >
              Erneut versuchen
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TapToPayDialog;
